import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiGrid,
  FiDatabase,
  FiMapPin,
  FiPackage,
  FiMessageSquare,
  FiLayers,
  FiArrowUpRight,
  FiCheckCircle,
  FiXCircle,
  FiTable,
} from "react-icons/fi";

import { useGlobalContext } from "../Context/GlobalContext";
import InsertionDetails from "./InsertionDetails";
import { pageInfo } from "./InsertionHelper";
import "./SelectPage.css";

const SelectPage = () => {
  const { userData, setActiveInsertionPage, activeInsertionPage } =
    useGlobalContext();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Original filtering logic preserved.
  const DashboardPages = pageInfo.filter((p) =>
    p.TableName.startsWith("railsetu"),
  );

  const blocksPages = pageInfo.filter((p) => p.TableName.startsWith("blocks"));

  const StationMappingPages = pageInfo.filter((p) =>
    p.TableName.startsWith("Station_Mapping_data"),
  );

  const CRISPages = pageInfo.filter((p) =>
    p.TableName.startsWith("cris_api_data"),
  );

  const DynamicTables = pageInfo.filter((p) =>
    p.TableName.startsWith("dynamicTable"),
  );

  const cls = pageInfo.filter((p) => p.TableName.startsWith("CLS"));
  const store = pageInfo.filter((p) => p.TableName.startsWith("store"));

  // Added Messages category.
  const messagePages = pageInfo.filter((p) =>
    p.TableName.startsWith("Message"),
  );

  const tabs = [
    { id: "Dashboard", label: "Dashboard Tables", icon: FiGrid },
    { id: "blocks", label: "Block Data", icon: FiDatabase },
    { id: "cris_api_data", label: "CRIS Tables", icon: FiTable },
    {
      id: "Station_Mapping_data",
      label: "Station Mapping",
      icon: FiMapPin,
    },
    { id: "CLS", label: "CLS Tables", icon: FiLayers },
    { id: "Store", label: "Store", icon: FiPackage },
    { id: "Message", label: "Messages", icon: FiMessageSquare },
    {
      id: "Dynamic Insertion",
      label: "Master Insertion",
      icon: FiDatabase,
    },
  ];

  const isCris = activeInsertionPage === "cris_api_data";

  const renderTable = (title, data) => (
    <section className="rs-data-card">
      <div className="rs-card-heading">
        <div className="rs-heading-icon">
          <FiTable size={22} />
        </div>

        <div className="rs-heading-copy">
          <h2>{title}</h2>
          <p>
            Manage railway records, review recent entries, and access available
            operations.
          </p>
        </div>

        <div className="rs-record-count">
          <span>{data.length}</span>
          <small>{data.length === 1 ? "Page" : "Pages"}</small>
        </div>
      </div>

      <div className="table-responsive rs-table-wrap">
        <table className="table align-middle mb-0 rs-table">
          <thead>
            <tr>
              <th className="rs-sno">#</th>
              <th className="text-start">Page / Module</th>
              <th className="rs-entry-column text-center">
                Last 30 Days Entry
              </th>
              {!isCris && <th className="text-center">Access Right</th>}
              {!isCris && <th className="text-center">Action</th>}
            </tr>
          </thead>

          <tbody>
            {data.length > 0 ? (
              data.map((pageData, index) => {
                const endpoint = pageData.pageUrl
                  .split("/")
                  .filter(Boolean)
                  .pop();
                const hasAccess = String(userData?.accessRight || "")
                  .split(",")
                  .map((item) => item.trim())
                  .includes(endpoint);

                const allowed = hasAccess || userData?.role === "admin";

                const isInsert = pageData.btnName === "Insert";

                return (
                  <tr key={`${pageData.TableName}-${pageData.pageUrl}`}>
                    <td className="rs-row-number">
                      {String(index + 1).padStart(2, "0")}
                    </td>

                    <td>
                      <div className="rs-page-name">
                        <span className="rs-page-icon">
                          <FiDatabase size={17} />
                        </span>
                        <span>{pageData.pageName.toUpperCase()}</span>
                      </div>
                    </td>

                    <td>
                      <div className="rs-entry-cell">
                        <InsertionDetails
                          pageData={pageData}
                          activeTab={activeInsertionPage}
                        />
                      </div>
                    </td>

                    {!isCris && (
                      <td className="text-center">
                        <span
                          style={{ fontSize: "13px" }}
                          className={`rs-access-status ${
                            allowed ? "rs-access-yes" : "rs-access-no"
                          }`}
                        >
                          {allowed ? (
                            <FiCheckCircle size={15} />
                          ) : (
                            <FiXCircle size={15} />
                          )}
                          {allowed ? "Granted" : "Restricted"}
                        </span>
                      </td>
                    )}

                    {!isCris && (
                      <td className="text-center" style={{ fontSize: "13px" }}>
                        {allowed ? (
                          <Link
                            to={pageData.pageUrl}
                            state={{
                              payload: {
                                tableName: pageData.TableName,
                              },
                            }}
                            className={`rs-action-btn ${
                              isInsert ? "rs-action-insert" : "rs-action-update"
                            }`}
                          >
                            <spcan style={{ fontSize: "13px" }}>
                              {pageData.btnName}
                              <FiArrowUpRight size={16} />
                            </spcan>
                          </Link>
                        ) : (
                          <span
                            className="rs-no-action"
                            style={{ fontSize: "12px" }}
                          >
                            No access
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={isCris ? 3 : 5}>
                  <div className="rs-empty-state">
                    <FiDatabase size={32} />
                    <h5>No pages available</h5>
                    <p>No configured pages were found in this category.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rs-table-footer">
        <span>
          Showing <strong>{data.length}</strong> configured{" "}
          {data.length === 1 ? "page" : "pages"}
        </span>

        <span className="rs-footer-indicator">
          <span />
          Rail-Setu Data Management
        </span>
      </div>
    </section>
  );

  return (
    <main className="rs-select-page">
      <div className="rs-page-container">
        <header className="rs-page-intro">
          <div>
            <span className="rs-eyebrow">RAIL-SETU / ADMINISTRATION</span>
            <div className="rs-intro-icon d-flex align-items-center">
              <FiDatabase size={25} />
              <h1>Data Management</h1>
            </div>
            <p>Access and manage railway data modules from one place.</p>
          </div>
        </header>

        <nav className="rs-category-nav" aria-label="Data categories">
          {tabs.map(({ id, label, icon: Icon }) => {
            const active = activeInsertionPage === id;

            return (
              <button
                type="button"
                key={id}
                className={`rs-category-tab ${
                  active ? "rs-category-active" : ""
                }`}
                onClick={() => setActiveInsertionPage(id)}
                aria-pressed={active}
              >
                <Icon size={17} />
                <span style={{ color: "black", fontSize: "15px" }}>
                  {label}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="rs-content-area">
          {activeInsertionPage === "Dashboard" &&
            renderTable("Dashboard Tables", DashboardPages)}

          {activeInsertionPage === "blocks" &&
            renderTable("Block Data Tables", blocksPages)}

          {activeInsertionPage === "cris_api_data" &&
            renderTable("CRIS Tables", CRISPages)}

          {activeInsertionPage === "Station_Mapping_data" &&
            renderTable("Station Mapping", StationMappingPages)}

          {activeInsertionPage === "Dynamic Insertion" &&
            renderTable("Master Insertion", DynamicTables)}

          {activeInsertionPage === "CLS" && renderTable("CLS Tables", cls)}

          {activeInsertionPage === "Store" && renderTable("Store", store)}

          {activeInsertionPage === "Message" &&
            renderTable("Messages", messagePages)}
        </div>

        <footer className="rs-page-footer">
          <span>RAIL-SETU</span>
          <span>Railway Data &amp; Administration Portal</span>
        </footer>
      </div>
    </main>
  );
};

export default SelectPage;
